import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import { ElevatedButton, TextInput } from '@producer/ui-kit';

import { FilterButton } from '../../assets/components/filter-button';
import { Plus, Search } from '../../../components/icons';
import { LoaderContainer } from '../../../routes/project/components/step-container';
import { PageLoader } from '../../../app/components/page-loader';
import { PagePlaceholder } from '../../../components/page-placeholder/page-placeholder';
import { CandidatesList } from './candidates-list';
import { Candidate } from '../../../app/entities/candidate';
import { SORTING_TYPES } from '../../../assets/enums/assetType.enum';
import { AssignContactsModal } from '../../../features/contacts/components/assign-contacts.modal';
import { Character } from '../../../app/entities/character';

import type { Contact } from '../../../app/entities/contact';
import { ToolBar, Tools } from '../../../components/toolbar/toolbar';

import styles from './candidates-tab.module.scss';
export type CandidatesTabProps = {
  characterId: string;
  spaceId: string;
};

export const CandidatesTab: React.FC<CandidatesTabProps> = observer(({ characterId, spaceId }) => {
  const { t } = useTranslation('gear');

  const character = Character.getOne(characterId)!;
  const castId = character?.cast?.candidateId;

  const [searchInput, setSearchInput] = useState('');
  const [sortBy, setSortBy] = useState(SORTING_TYPES.A_TO_Z);
  const [filteredCandidates, setFilteredCandidates] = useState<Candidate[]>([]);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);

  useEffect(() => {
    setFilteredCandidates(character.candidates);
  }, [character.candidates]);

  type SearchField = 'firstName' | 'lastName';
  type SearchFieldsProps = Array<keyof Pick<Contact, SearchField>>;

  const startSearch = useCallback(
    async (value: string) => {
      if (!character) {
        return;
      }

      const searchFields: SearchFieldsProps = ['firstName', 'lastName'];

      const filteredCandidates = character.candidates.filter((candidate) =>
        searchFields.some((searchField) =>
          value
            .split(' ')
            .some((inputValue: string) =>
              candidate[searchField]?.toLowerCase().includes(inputValue.toLowerCase()),
            ),
        ),
      );
      setFilteredCandidates(filteredCandidates);
    },
    [character],
  );

  const hasCharacters = !!character?.candidates?.length;

  const handleDelete = (candidateId: string) => {
    character.removeCandidate(candidateId);
  };

  const openCandidateModal = () => {
    setIsAssignModalOpen(true);
  };

  return (
    <>
      <div className={styles['container']}>
        <ToolBar title={'Candidates'}>
          <Tools>
            <TextInput
              key="search-candidates"
              value={searchInput}
              icon={<Search />}
              placeholder={t('search')}
              onChange={(e) => {
                const value = e.currentTarget.value;
                setSearchInput(value);
                startSearch(value);
              }}
            />
            <FilterButton
              key="candidates-buttons"
              sortTypes={[SORTING_TYPES.A_TO_Z, SORTING_TYPES.Z_TO_A]}
              value={sortBy}
              onSelect={setSortBy}
            />
            <ElevatedButton
              key="add-candidates"
              icon={<Plus />}
              text={'Add candidate'}
              onClick={openCandidateModal}
            />
          </Tools>
        </ToolBar>

        {!character?.candidates ? (
          <LoaderContainer>
            <PageLoader />
          </LoaderContainer>
        ) : hasCharacters ? (
          <CandidatesList candidates={filteredCandidates} castId={castId} sortBy={sortBy} />
        ) : (
          <PagePlaceholder
            backgroundImgUrl="/images/Process_Badges_Pre_Production.svg"
            description='Candidates will be displayed here, you can add them by clicking on the "Add candidate" button'
            actionButton={{ text: 'Add Candidate', onClick: openCandidateModal }}
          />
        )}
      </div>
      {isAssignModalOpen && (
        <AssignContactsModal
          title={'Add Candidates'}
          subtitle={'Added candidates'}
          addedContacts={(character?.candidates as any) || []}
          onAdd={character.addCandidate}
          onDelete={handleDelete}
          spaceId={spaceId}
          onCancel={() => setIsAssignModalOpen(false)}
        />
      )}
    </>
  );
});
